import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const isLocalPreview = process.env.REACT_APP_LOCAL_PREVIEW === "true";
  const [notice] = useState(() => { try { const value = sessionStorage.getItem("session_notice"); sessionStorage.removeItem("session_notice"); return value; } catch { return null; } });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      user.role === "b2b_musteri" ? navigate("/b2b") : navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {notice && <div role="status" className="fixed top-2 left-2 right-2 z-50 rounded-lg bg-amber-50 p-3 text-center text-sm text-amber-900">{notice}</div>}
      {/* Hero — arka plan net görünsün, içerik sol-alt köşe */}
      <div
        className="hidden lg:flex flex-1 bg-cover bg-center relative overflow-hidden"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1609903730357-fc7951f05910?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
      >
        {/* Gradient: sadece alt sol köşeyi örter, ortadaki far görselini açıkta bırakır */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(to top, rgba(10,18,40,0.82) 0%, rgba(10,18,40,0.45) 35%, rgba(10,18,40,0.08) 65%, transparent 100%)",
          }}
        />
        {/* Sol kenar için ince yatay gradient — metin okunabilirliği */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(to right, rgba(10,18,40,0.45) 0%, transparent 55%)",
          }}
        />

        {/* İçerik — sol alt köşe */}
        <div className="relative z-10 flex flex-col justify-end items-start p-10 pb-12 text-white w-full">

          {/* VAGenpar logo — büyük, az padding */}
          <div className="mb-5">
            <div className="inline-flex items-center bg-white rounded-xl px-4 py-3 shadow-xl mb-3">
              <BrandLogo className="h-10 w-auto" eager />
            </div>
            <div className="text-[11px] text-white/55 uppercase tracking-[0.2em] font-medium">
              ERP + B2B Portal
            </div>
          </div>

          <p className="text-white/75 text-sm max-w-[300px] leading-relaxed mb-7 font-light">
            Volkswagen Grubu yedek parça operasyonları için entegre yönetim ve B2B sipariş platformu.
          </p>

          {/* Uzmanlık alanları — sade metin */}
          <div>
            <p className="text-[10px] font-semibold text-white/45 uppercase tracking-[0.25em] mb-3">
              Uzmanlık Alanımız:
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {["Volkswagen", "Audi", "Seat", "Cupra", "Skoda", "Porsche"].map((brand) => (
                <span
                  key={brand}
                  className="text-sm font-light text-white/85 tracking-wide"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {brand}
                </span>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Form */}
      <div className="flex-1 lg:max-w-[420px] flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">

          {/* Vektörel logo — tüm ekran boyutlarında keskin ve tutarlı */}
          <div className="flex justify-center mb-8">
            <BrandLogo className="h-11 w-auto" eager />
          </div>

          <h1 className="text-3xl font-bold text-[#0D1B3E] mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Giriş Yap
          </h1>
          <p className="text-slate-500 text-sm mb-8">Hesabınıza erişmek için giriş yapın</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-[0.1em] mb-1.5">
                E-Posta
              </label>
              <input
                data-testid={AUTH.emailInput}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@firma.com"
                required
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-[0.1em] mb-1.5">
                Şifre
              </label>
              <input
                data-testid={AUTH.passwordInput}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors"
              />
            </div>
            <button
              data-testid={AUTH.submitButton}
              type="submit"
              disabled={loading}
              className="w-full bg-[#0D1B3E] text-white rounded-md px-4 py-2.5 font-medium hover:bg-[#172A58] active:scale-[0.98] transition-all disabled:opacity-60 text-sm mt-2"
            >
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </form>

          {isLocalPreview && <div className="mt-5 rounded-lg border border-blue-100 bg-blue-50 p-3">
            <p className="mb-2 text-xs font-semibold text-[#0D1B3E]">Yerel test hesapları</p>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => { setEmail("admin@vagenpar.local"); setPassword("VAGenpar-Admin-2026!Local"); }} className="rounded-md border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700">Admin hesabı</button>
              <button type="button" onClick={() => { setEmail("test@vagenpar.local"); setPassword("VAGenpar-Test-2026!Local"); }} className="rounded-md border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700">Müşteri hesabı</button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">Hesabı seçin, ardından Giriş Yap düğmesine basın.</p>
          </div>}

          <p className="text-center text-xs text-slate-400 mt-8">
            VAGenpar ERP v1.0 — Tüm hakları saklıdır
          </p>
        </div>
      </div>
    </div>
  );
}
